import { useCallback, useEffect, useRef, useState } from "react";
import { PortalApiError, portalFetch } from "../api/client";
import { usePagedPortalItems } from "../hooks/usePagedPortalItems";
import type {
	PortalAcceptanceTrackerDto,
	PortalDeliverySettingsDto,
	PortalExecutionDto,
	PortalPullRequestPublicationDto,
	PortalSpecReadinessDto,
	PortalWorkMapDto,
} from "../model";
import { calculateDeliveryProgress } from "./deliveryProgress";
import { PortalSelect } from "./PortalSelect";
import { RichText } from "./RichText";
import { StatusBanner } from "./StatusBanner";
import { humanizePortalStatus } from "./statusLabels";

const ACTIONS = [
	["majorInfrastructure", "Major infrastructure"],
	["permissionChange", "Permission change"],
	["crossProjectAccess", "Cross-project access"],
	["publicRelease", "Public release"],
	["archive", "Archive project"],
	["restore", "Restore project"],
] as const;

export function ProjectDelivery({
	projectId,
	mapId,
	specId,
	readiness,
	acceptanceTracker,
	onOpenSpec,
	onContinueDiscovery,
}: {
	projectId: string;
	mapId: string;
	specId: string;
	readiness: PortalSpecReadinessDto;
	acceptanceTracker: PortalAcceptanceTrackerDto | null;
	onOpenSpec: () => void;
	onContinueDiscovery: () => void;
}) {
	const [status, setStatus] = useState("");
	const [worktreeCopyStatus, setWorktreeCopyStatus] = useState<Record<string, "success" | "error" | undefined>>({});
	const [requesting, setRequesting] = useState<"selected" | "sequence" | null>(null);
	const [deliveryMap, setDeliveryMap] = useState<PortalWorkMapDto>();
	const [selectedTicketId, setSelectedTicketId] = useState<string>();
	const [deliverySettings, setDeliverySettings] = useState<PortalDeliverySettingsDto>();
	const [selectedRequiredSkills, setSelectedRequiredSkills] = useState<string[]>([]);
	const [skillsStatus, setSkillsStatus] = useState("");
	const [skillsSaving, setSkillsSaving] = useState(false);
	const iterationAttempt = useRef<{ workTicketId?: string; key: string } | undefined>(undefined);
	const sequenceAttempt = useRef<string | undefined>(undefined);
	const [publication, setPublication] = useState<PortalPullRequestPublicationDto>();
	const [publishing, setPublishing] = useState(false);
	const publicationAttempt = useRef<string | undefined>(undefined);
	const executionPage = usePagedPortalItems<PortalExecutionDto>(`/api/portal/v1/projects/${projectId}/executions`);
	const { refresh: refreshExecutions } = executionPage;
	const executions = executionPage.items.filter(
		(execution) => execution.specRevisionHash === acceptanceTracker?.specRevisionHash,
	);
	const iterations = executions.filter((execution) => execution.kind === "iteration");
	const latestPublication = executions.find((execution) => execution.kind === "pullRequestPublication");
	// Scheduling is the server's decision: this map only blocks its own actions, so a
	// different map stays queueable while this one runs.
	const mapTicketIds = new Set(deliveryMap?.tickets.map((ticket) => ticket.id) ?? []);
	const mapIsActive = executions.some(
		(execution) =>
			(execution.status === "queued" || execution.status === "running") &&
			execution.workTicketId !== undefined &&
			mapTicketIds.has(execution.workTicketId),
	);
	const frontier = deliveryMap?.tickets.filter((ticket) => ticket.isFrontier) ?? [];
	const ownerFrontier = frontier.filter((ticket) => ticket.participation === "human");
	const ownerEvidenceCriteria =
		acceptanceTracker?.criteria.filter((criterion) => criterion.ownerEvidencePending) ?? [];
	const deliveryFrontier = frontier.filter(
		(ticket) => !ownerFrontier.some((ownerTicket) => ownerTicket.id === ticket.id),
	);
	const ownerEvidenceForExecution = (execution: PortalExecutionDto) =>
		ownerEvidenceCriteria.filter((criterion) => criterion.lastExecutionId === execution.id);
	const selectedTicket = deliveryFrontier.find((ticket) => ticket.id === selectedTicketId) ?? deliveryFrontier[0];
	const progress = deliveryMap ? calculateDeliveryProgress(deliveryMap.tickets) : undefined;
	const savedRequiredSkills = deliverySettings?.requiredSkills ?? [];
	const skillsChanged =
		deliverySettings !== undefined && JSON.stringify(selectedRequiredSkills) !== JSON.stringify(savedRequiredSkills);
	const availableSkillAliases = new Set(deliverySettings?.availableSkills.map((skill) => skill.alias) ?? []);
	const unavailableRequiredSkills = selectedRequiredSkills.filter((alias) => !availableSkillAliases.has(alias));
	const selectableSkills = [
		...(deliverySettings?.availableSkills ?? []),
		...unavailableRequiredSkills.map((alias) => ({ alias, status: "experimental" as const, category: undefined })),
	];
	const refreshDeliverySettings = useCallback(async () => {
		const settings = await portalFetch<PortalDeliverySettingsDto>(
			`/api/portal/v1/projects/${projectId}/delivery-settings`,
		);
		setDeliverySettings(settings);
		setSelectedRequiredSkills(settings.requiredSkills);
	}, [projectId]);
	useEffect(() => {
		setDeliverySettings(undefined);
		setSelectedRequiredSkills([]);
		setSkillsStatus("");
		void refreshDeliverySettings().catch(() => setSkillsStatus("Required skills could not be loaded."));
	}, [refreshDeliverySettings]);
	const refreshDelivery = useCallback(async () => {
		if (readiness.stage !== "readyForDelivery") return;
		const synced = await portalFetch<PortalWorkMapDto>(
			`/api/portal/v1/projects/${projectId}/work-maps/delivery/sync?mapId=${encodeURIComponent(mapId)}`,
			{ method: "POST", body: "{}" },
		);
		setDeliveryMap(synced);
		setSelectedTicketId((current) =>
			synced.tickets.some((ticket) => ticket.id === current && ticket.isFrontier && ticket.participation !== "human")
				? current
				: synced.tickets.find((ticket) => ticket.isFrontier && ticket.participation !== "human")?.id,
		);
	}, [mapId, projectId, readiness.stage]);
	useEffect(() => {
		void refreshDelivery().catch(() => setStatus("The delivery plan could not be loaded."));
	}, [refreshDelivery]);
	const deliveryMapId = deliveryMap?.id;
	// Eligibility and blocking reasons are the server's decision; the page renders them as returned.
	const refreshPublication = useCallback(async () => {
		if (!deliveryMapId) return;
		setPublication(
			await portalFetch<PortalPullRequestPublicationDto>(
				`/api/portal/v1/projects/${projectId}/work-maps/${deliveryMapId}/pull-request`,
			),
		);
	}, [deliveryMapId, projectId]);
	useEffect(() => {
		setPublication(undefined);
		void refreshPublication().catch(() => setStatus("Pull request status could not be loaded."));
	}, [refreshPublication]);
	useEffect(() => {
		// Keep observing server-owned publication across startup recovery and retry
		// backoff, when every execution in the current page may already be terminal.
		const awaitingPublication = publication?.eligible && !publication.readyForReview;
		if (
			!awaitingPublication &&
			!executions.some((execution) => execution.status === "queued" || execution.status === "running")
		)
			return;
		const timer = window.setInterval(
			() =>
				void refreshExecutions()
					.then(() => Promise.all([refreshDelivery(), refreshPublication()]))
					.catch(() => setStatus("Iteration history is unavailable.")),
			2_000,
		);
		return () => window.clearInterval(timer);
	}, [executions, publication, refreshDelivery, refreshExecutions, refreshPublication]);

	async function copyWorktreePath(execution: PortalExecutionDto) {
		if (execution.worktreePath === undefined) return;
		setWorktreeCopyStatus((current) => ({ ...current, [execution.id]: undefined }));
		try {
			await navigator.clipboard.writeText(execution.worktreePath);
			setWorktreeCopyStatus((current) => ({ ...current, [execution.id]: "success" }));
		} catch {
			setWorktreeCopyStatus((current) => ({ ...current, [execution.id]: "error" }));
		}
	}

	async function openPullRequest() {
		if (!deliveryMapId) return;
		setStatus("");
		setPublishing(true);
		// A second press or a retry after a lost response reuses the key, so the server
		// returns the same execution instead of starting another.
		publicationAttempt.current ??= crypto.randomUUID();
		try {
			await portalFetch<PortalExecutionDto>(
				`/api/portal/v1/projects/${projectId}/work-maps/${deliveryMapId}/pull-request`,
				{ method: "POST", headers: { "Idempotency-Key": publicationAttempt.current }, body: "{}" },
			);
			publicationAttempt.current = undefined;
		} catch {
			setStatus("The pull request could not be started. Try again; a retry reuses the same attempt.");
		} finally {
			setPublishing(false);
			await Promise.all([refreshExecutions(), refreshPublication()]).catch(() => undefined);
		}
	}

	async function startIteration(workTicketId?: string) {
		setStatus("");
		setRequesting("selected");
		if (!iterationAttempt.current || iterationAttempt.current.workTicketId !== workTicketId)
			iterationAttempt.current = { workTicketId, key: crypto.randomUUID() };
		const attempt = iterationAttempt.current;
		try {
			const execution = await portalFetch<PortalExecutionDto>(`/api/portal/v1/projects/${projectId}/executions`, {
				method: "POST",
				headers: { "Idempotency-Key": attempt.key },
				body: JSON.stringify({ kind: "iteration", specId, ...(workTicketId ? { workTicketId } : {}) }),
			});
			iterationAttempt.current = undefined;
			if (execution.status === "failed" || execution.status === "cancelled") {
				setStatus("The iteration did not start. You can try this slice again.");
				await refreshExecutions().catch(() => undefined);
				return;
			}
			setStatus(workTicketId ? "Selected delivery slice requested." : "Iteration requested.");
			try {
				await Promise.all([refreshExecutions(), refreshDelivery()]);
			} catch {
				setStatus(
					workTicketId
						? "Selected delivery slice started. Its latest status will appear when this page reconnects."
						: "Iteration started. Its latest status will appear when this page reconnects.",
				);
			}
		} catch (error) {
			setStatus(
				error instanceof PortalApiError
					? error.message
					: "The iteration request could not be confirmed. Refresh the delivery plan before retrying.",
			);
			await refreshExecutions().catch(() => undefined);
		} finally {
			setRequesting(null);
		}
	}

	async function startSequence() {
		if (!deliveryMap) return;
		setStatus("");
		setRequesting("sequence");
		sequenceAttempt.current ??= crypto.randomUUID();
		try {
			const result = await portalFetch<{
				executions: PortalExecutionDto[];
				queued?: { mapId: string; reason: "capacity" | "blocked" };
			}>(`/api/portal/v1/projects/${projectId}/executions/sequence`, {
				method: "POST",
				headers: { "Idempotency-Key": sequenceAttempt.current },
				body: JSON.stringify({ specId, mapId: deliveryMap.id }),
			});
			sequenceAttempt.current = undefined;
			const failed = result.executions.some(
				(execution) => execution.status === "failed" || execution.status === "cancelled",
			);
			setStatus(
				result.queued
					? result.queued.reason === "blocked"
						? "This map is queued. It starts automatically once the maps it depends on finish."
						: "This map is queued. It starts automatically as soon as delivery capacity frees up."
					: failed
						? "The sequence could not schedule every slice. Failed slices remain visible below."
						: `Sequence requested for ${result.executions.length} delivery ${result.executions.length === 1 ? "slice" : "slices"}.`,
			);
			await Promise.all([refreshExecutions(), refreshDelivery()]).catch(() => undefined);
		} catch (error) {
			setStatus(
				error instanceof PortalApiError
					? error.message
					: "The sequence request could not be confirmed. Refresh the delivery plan before retrying.",
			);
			await refreshExecutions().catch(() => undefined);
		} finally {
			setRequesting(null);
		}
	}

	function toggleRequiredSkill(alias: string) {
		setSelectedRequiredSkills((current) =>
			(current.includes(alias) ? current.filter((item) => item !== alias) : [...current, alias]).sort(
				(left, right) => left.localeCompare(right),
			),
		);
		setSkillsStatus("");
	}

	async function saveRequiredSkills() {
		setSkillsSaving(true);
		setSkillsStatus("");
		try {
			const settings = await portalFetch<PortalDeliverySettingsDto>(
				`/api/portal/v1/projects/${projectId}/delivery-settings`,
				{
					method: "PUT",
					body: JSON.stringify({ requiredSkills: selectedRequiredSkills }),
				},
			);
			setDeliverySettings(settings);
			setSelectedRequiredSkills(settings.requiredSkills);
			setSkillsStatus(
				settings.requiredSkills.length === 0
					? "Delivery runs no longer require project-specific skills."
					: `${settings.requiredSkills.length} required ${settings.requiredSkills.length === 1 ? "skill" : "skills"} saved for future delivery runs.`,
			);
		} catch {
			setSkillsStatus("Required skills could not be saved. Nothing was changed.");
		} finally {
			setSkillsSaving(false);
		}
	}

	return (
		<section className="project-delivery" aria-labelledby="iteration-heading">
			<p className="eyebrow">DELIVERY</p>
			<h2 id="iteration-heading">Project iterations</h2>
			<section className="delivery-skills" aria-labelledby="delivery-skills-heading">
				<div className="delivery-skills-heading">
					<div>
						<p className="eyebrow">DEVELOPMENT TOOLING</p>
						<h3 id="delivery-skills-heading">Required skills</h3>
						<p>Every new delivery iteration will start with the selected workspace skills.</p>
					</div>
					<strong>
						<span aria-hidden="true">{selectedRequiredSkills.length.toString().padStart(2, "0")}</span>
						<span className="visually-hidden">{selectedRequiredSkills.length} required skills</span>
					</strong>
				</div>
				{deliverySettings ? (
					selectableSkills.length > 0 ? (
						<fieldset>
							<legend>Select skills required for development</legend>
							<div className="delivery-skill-list">
								{selectableSkills.map((skill) => {
									const unavailable = !availableSkillAliases.has(skill.alias);
									return (
										<label key={skill.alias} className={unavailable ? "unavailable" : undefined}>
											<input
												type="checkbox"
												checked={selectedRequiredSkills.includes(skill.alias)}
												onChange={() => toggleRequiredSkill(skill.alias)}
											/>
											<span>
												<strong>{skill.alias}</strong>
												<small>
													{unavailable
														? "No longer registered — deselect to remove"
														: [skill.category, skill.status].filter(Boolean).join(" · ")}
												</small>
											</span>
										</label>
									);
								})}
							</div>
						</fieldset>
					) : (
						<p className="delivery-skills-empty">No workspace skills are registered yet.</p>
					)
				) : (
					<p className="delivery-skills-empty">Loading the workspace skill registry…</p>
				)}
				<div className="delivery-skills-actions">
					<button
						type="button"
						disabled={!skillsChanged || skillsSaving || unavailableRequiredSkills.length > 0}
						onClick={() => void saveRequiredSkills()}
					>
						{skillsSaving ? "Saving…" : "Save required skills"}
					</button>
					<small>Changes apply to future runs; active iterations keep their current skill set.</small>
				</div>
				{skillsStatus && (
					<StatusBanner kind={skillsStatus.includes("could not") ? "error" : "success"}>
						{skillsStatus}
					</StatusBanner>
				)}
			</section>
			{readiness.stage === "ticketPlanning" ? (
				<div className="delivery-gate" data-owner="administrator">
					<strong>
						{readiness.status === "blocked" ? "Ticket planning needs attention" : "Preparing delivery tickets"}
					</strong>
					<p>{readiness.planningSummary}</p>
					<p>Your Spec is approved. Delivery remains locked until independent ticket-plan review succeeds.</p>
					<button type="button" disabled>
						Request an iteration
					</button>
				</div>
			) : readiness.stage !== "readyForDelivery" ? (
				<div
					className="delivery-gate"
					data-owner={readiness.stage === "adminReview" ? "administrator" : "project-owner"}
				>
					<span className="delivery-gate-owner">
						{readiness.stage === "adminReview" ? "Waiting on administrator" : "Your next step"}
					</span>
					<strong>
						{readiness.stage === "adminReview"
							? "Your revised spec is waiting for approval."
							: "Delivery begins after the spec is complete and approved."}
					</strong>
					<p>
						{readiness.stage === "adminReview"
							? "The administrator must review the exact changes and make the delivery decision. You do not need to do anything until that review is complete."
							: "Continue discovery with the Project agent. Iterations stay unavailable so implementation cannot outrun the decisions it depends on."}
					</p>
					<div className="delivery-gate-actions">
						<button type="button" disabled aria-describedby="delivery-gate-reason">
							Request an iteration
						</button>
						<button type="button" className="secondary" onClick={onContinueDiscovery}>
							{readiness.stage === "adminReview" ? "View spec awaiting review" : "Continue spec discovery"}
						</button>
					</div>
					<small id="delivery-gate-reason">
						{readiness.stage === "adminReview"
							? "Unavailable until the administrator approves this spec revision."
							: "Unavailable until discovery and administrator approval are complete."}
					</small>
				</div>
			) : (
				<>
					<div className="delivery-plan-heading">
						<div>
							<p className="eyebrow">APPROVED SPEC DELIVERY</p>
							<h3>Choose a ready delivery slice</h3>
							<p>
								The approved Spec has already been split into dependency-linked slices. Only work whose
								prerequisites are complete appears below; anything needing evidence only you can provide is
								separated under Your validation.
							</p>
						</div>
						{progress && (
							<div className="delivery-plan-total">
								<strong>
									{progress.completed} / {progress.total}
								</strong>
								<span>complete</span>
							</div>
						)}
					</div>
					{deliveryMap ? (
						<div className="delivery-slices">
							{deliveryFrontier.length > 0 ? (
								<fieldset>
									<legend>Ready for the delivery agent</legend>
									{deliveryFrontier.map((ticket, index) => (
										<label
											key={ticket.id}
											className={selectedTicket?.id === ticket.id ? "selected" : undefined}
										>
											<input
												type="radio"
												name="delivery-slice"
												checked={selectedTicket?.id === ticket.id}
												onChange={() => setSelectedTicketId(ticket.id)}
											/>
											<span className="delivery-slice-number">{String(index + 1).padStart(2, "0")}</span>
											<span>
												<strong>
													<RichText content={ticket.title} inline />
												</strong>
												<small>
													<RichText content={ticket.acceptanceCriteria[0] ?? ""} inline />
												</small>
											</span>
										</label>
									))}
								</fieldset>
							) : ownerFrontier.length === 0 && ownerEvidenceCriteria.length === 0 ? (
								<div className="delivery-plan-complete">
									<strong>Every current delivery slice is resolved.</strong>
									<p>
										Run a completion review to confirm the whole approved Spec, including external validation.
									</p>
								</div>
							) : null}
							{ownerEvidenceCriteria.length > 0 && deliveryFrontier.length === 0 && (
								<div className="delivery-plan-complete">
									<strong>Automated delivery complete.</strong>
									<p>
										Acceptance remains incomplete until you record the requested validation evidence below.
									</p>
								</div>
							)}
							{(ownerFrontier.length > 0 || ownerEvidenceCriteria.length > 0) && (
								<section className="delivery-owner-validation" aria-labelledby="owner-validation-heading">
									<div>
										<p className="eyebrow">YOUR VALIDATION</p>
										<h4 id="owner-validation-heading">Evidence only you can provide</h4>
										<p>
											These are not coding slices. Record the requested observation or measurement in the
											Spec; builder runs will not supply it.
										</p>
									</div>
									<ul>
										{ownerEvidenceCriteria.map((criterion) => (
											<li key={criterion.criterionKey}>
												<strong>
													<RichText content={criterion.text} inline />
												</strong>
												{criterion.nextAction && <small>{criterion.nextAction}</small>}
											</li>
										))}
										{ownerFrontier.map((ticket) => (
											<li key={ticket.id}>
												<strong>
													<RichText content={ticket.title} inline />
												</strong>
												<small>
													<RichText content={ticket.acceptanceCriteria[0] ?? ""} inline />
												</small>
											</li>
										))}
									</ul>
									<button type="button" className="secondary" onClick={onOpenSpec}>
										Record validation evidence
									</button>
								</section>
							)}
							{publication && (
								<section className="delivery-pull-request" aria-labelledby="pull-request-heading">
									<div>
										<p className="eyebrow">PUBLICATION</p>
										<h4 id="pull-request-heading">
											{publication.readyForReview ? "Ready for review" : "Pull request"}
										</h4>
										<p>
											Delivery automatically opens or reuses one pull request after implementation and
											automated verification pass. Human acceptance and revisions follow the PR; merging is
											manual. Use this action only to recover publication or refresh its status.
										</p>
									</div>
									<button
										type="button"
										disabled={
											!publication.eligible ||
											publishing ||
											latestPublication?.status === "running" ||
											latestPublication?.status === "queued"
										}
										aria-busy={publishing}
										aria-describedby={publication.eligible ? undefined : "pull-request-blocking"}
										onClick={() => void openPullRequest()}
									>
										{publishing
											? "Starting workflow…"
											: publication.pullRequest
												? "Refresh PR status"
												: "Create PR (recovery)"}
									</button>
									{!publication.eligible && (
										<ul id="pull-request-blocking">
											{publication.blockingReasons.map((reason) => (
												<li key={`${reason.code}-${reason.criterionKey ?? ""}`}>{reason.message}</li>
											))}
										</ul>
									)}
									{latestPublication && (
										<div className="delivery-pull-request-run" data-status={latestPublication.status}>
											<div className="execution-heading">
												<strong>Latest publication run</strong>
												<span className="project-state">
													{humanizePortalStatus(latestPublication.status)}
												</span>
											</div>
											{latestPublication.sanitizedSummary && <p>{latestPublication.sanitizedSummary}</p>}
										</div>
									)}
									{publication.observation && (
										<p>
											PR {publication.observation.state} · Review:{" "}
											{humanizePortalStatus(publication.observation.reviewStatus)}
											<br />
											Last checked {new Date(publication.observation.checkedAt).toLocaleString()}
											{publication.observation.mergedAt && (
												<> · Merged {new Date(publication.observation.mergedAt).toLocaleString()}</>
											)}
										</p>
									)}
									{publication.pullRequest && (
										<p className="delivery-pull-request-link">
											<a href={publication.pullRequest.url} target="_blank" rel="noreferrer">
												{publication.pullRequest.repository} #{publication.pullRequest.number}
											</a>
										</p>
									)}
								</section>
							)}
							<div className="delivery-plan-actions">
								<button
									type="button"
									disabled={!selectedTicket || requesting !== null || mapIsActive}
									onClick={() => void startIteration(selectedTicket?.id)}
								>
									{requesting === "selected" ? "Building slice…" : "Build this ready slice"}
								</button>
								<button
									type="button"
									className="secondary"
									disabled={deliveryFrontier.length === 0 || requesting !== null || mapIsActive}
									onClick={() => void startSequence()}
								>
									{requesting === "sequence" ? "Starting sequence…" : "Sequence all"}
								</button>
							</div>
						</div>
					) : (
						<p>Loading the dependency-linked delivery plan…</p>
					)}
				</>
			)}
			<h3 className="iteration-history-heading">Iteration history</h3>
			{iterations.length > 0 ? (
				<ul className="execution-list">
					{iterations.map((execution) => {
						const pendingOwnerEvidence = ownerEvidenceForExecution(execution);
						const waitingOnOwner = pendingOwnerEvidence.length > 0;
						return (
							<li key={execution.id}>
								<div className="execution-heading">
									<strong>{humanizePortalStatus(execution.kind)}</strong>
									<span className="project-state">
										{waitingOnOwner ? "Automated delivery complete" : humanizePortalStatus(execution.status)}
									</span>
								</div>
								{waitingOnOwner ? (
									<p>
										Automated delivery complete; waiting on owner evidence.{" "}
										{pendingOwnerEvidence
											.map((criterion) => criterion.nextAction)
											.filter((action): action is string => Boolean(action))
											.join(" ")}
									</p>
								) : (
									execution.sanitizedSummary && <p>{execution.sanitizedSummary}</p>
								)}
								{execution.worktreePath !== undefined ? (
									<div className="execution-worktree">
										<div className="execution-worktree-location">
											<small>Worktree</small>
											<span id={`worktree-${execution.id}`} className="execution-worktree-path">
												{execution.worktreePath}
											</span>
										</div>
										<button
											type="button"
											className="secondary execution-worktree-copy"
											aria-label="Copy worktree path"
											aria-describedby={`worktree-${execution.id}`}
											onClick={() => void copyWorktreePath(execution)}
										>
											<svg
												viewBox="0 0 24 24"
												width="20"
												height="20"
												fill="none"
												stroke="currentColor"
												strokeWidth="1.8"
												aria-hidden="true"
											>
												<rect x="8" y="8" width="12" height="12" rx="2" />
												<path d="M16 8V4H4v12h4" />
											</svg>
										</button>
									</div>
								) : (
									<p>Worktree location unavailable</p>
								)}
								{worktreeCopyStatus[execution.id] && (
									<StatusBanner kind={worktreeCopyStatus[execution.id] === "success" ? "success" : "error"}>
										{worktreeCopyStatus[execution.id] === "success"
											? "Worktree path copied."
											: "Copy failed. Select the worktree path and copy it manually."}
									</StatusBanner>
								)}
								{execution.workTicketId && deliveryMap && (
									<small>
										{execution.sequencePosition && execution.sequenceSize
											? `Sequence ${execution.sequencePosition} of ${execution.sequenceSize} · Slice: `
											: "Slice: "}
										<RichText
											content={
												deliveryMap.tickets.find((ticket) => ticket.id === execution.workTicketId)?.title ??
												"Earlier approved criterion"
											}
											inline
										/>
									</small>
								)}
							</li>
						);
					})}
				</ul>
			) : (
				<small>No iterations have been requested yet.</small>
			)}
			{status && (
				<StatusBanner
					kind={status.includes("requested") ? "success" : status.includes("could not") ? "error" : "info"}
				>
					{status}
				</StatusBanner>
			)}
			{executionPage.hasMore && (
				<button
					type="button"
					className="secondary"
					disabled={executionPage.loadingMore}
					onClick={() => void executionPage.loadMore()}
				>
					{executionPage.loadingMore ? "Loading…" : "Load more activity"}
				</button>
			)}
		</section>
	);
}

const COMPLETION_STATUS_LABELS = {
	met: "Met",
	partial: "Partial",
	unmet: "Unmet",
	blocked: "Blocked",
	unverified: "Unverified",
} as const;

export function ProjectCompletion({
	projectId,
	specId,
	readiness,
	acceptanceTracker,
}: {
	projectId: string;
	specId: string;
	readiness: PortalSpecReadinessDto;
	acceptanceTracker: PortalAcceptanceTrackerDto | null;
}) {
	const [status, setStatus] = useState("");
	const [requesting, setRequesting] = useState(false);
	const reviewAttempt = useRef<string | undefined>(undefined);
	const executionPage = usePagedPortalItems<PortalExecutionDto>(`/api/portal/v1/projects/${projectId}/executions`);
	const { refresh: refreshExecutions } = executionPage;
	const executions = executionPage.items.filter(
		(execution) => execution.specRevisionHash === acceptanceTracker?.specRevisionHash,
	);
	const specReviews = executions.filter((execution) => execution.kind === "specReview");
	const latestSpecReview = specReviews[0];
	const assessmentIsCurrent = Boolean(
		latestSpecReview?.specRevisionHash && latestSpecReview.specRevisionHash === acceptanceTracker?.specRevisionHash,
	);
	const assessment = assessmentIsCurrent ? latestSpecReview?.completionAssessment : undefined;
	const hasActiveExecution = executions.some(
		(execution) => execution.status === "queued" || execution.status === "running",
	);
	const latestSpecReviewStatus = latestSpecReview
		? latestSpecReview.status === "succeeded"
			? "Reviewed"
			: latestSpecReview.status === "failed"
				? "Review failed"
				: latestSpecReview.status === "running"
					? "Reviewing"
					: humanizePortalStatus(latestSpecReview.status)
		: null;
	const counts = assessment?.criteria.reduce<Record<string, number>>((result, criterion) => {
		result[criterion.status] = (result[criterion.status] ?? 0) + 1;
		return result;
	}, {});

	useEffect(() => {
		if (!hasActiveExecution) return;
		const timer = window.setInterval(
			() => void refreshExecutions().catch(() => setStatus("Completion status is temporarily unavailable.")),
			2_000,
		);
		return () => window.clearInterval(timer);
	}, [hasActiveExecution, refreshExecutions]);
	useEffect(() => {
		if (latestSpecReview?.status === "succeeded" || latestSpecReview?.status === "failed") setStatus("");
	}, [latestSpecReview?.status]);

	async function startReview() {
		setStatus("");
		setRequesting(true);
		reviewAttempt.current ??= crypto.randomUUID();
		try {
			const execution = await portalFetch<PortalExecutionDto>(`/api/portal/v1/projects/${projectId}/executions`, {
				method: "POST",
				headers: { "Idempotency-Key": reviewAttempt.current },
				body: JSON.stringify({ kind: "specReview", specId }),
			});
			reviewAttempt.current = undefined;
			if (execution.status === "failed" || execution.status === "cancelled") {
				setStatus("The completion review did not start. You can try again.");
				await refreshExecutions().catch(() => undefined);
				return;
			}
			setStatus("Completion review requested.");
			try {
				await refreshExecutions();
			} catch {
				setStatus("Completion review started. Its status will appear when this page reconnects.");
			}
		} catch (error) {
			setStatus(
				error instanceof PortalApiError
					? error.message
					: "The completion-review request could not be confirmed. Refresh its activity before retrying.",
			);
			await refreshExecutions().catch(() => undefined);
		} finally {
			setRequesting(false);
		}
	}

	return (
		<section className="project-completion" aria-labelledby="completion-heading">
			<div className="completion-review-heading">
				<div>
					<p className="eyebrow">SPEC COMPLETION</p>
					<h2 id="completion-heading">Is the spec complete?</h2>
					<p>Compare every acceptance criterion with the current source, verification, and commit history.</p>
				</div>
				<button
					type="button"
					disabled={!readiness.readyForExecution || requesting || hasActiveExecution}
					aria-describedby={!readiness.readyForExecution ? "completion-gate-reason" : undefined}
					onClick={() => void startReview()}
				>
					{requesting ? "Starting review…" : "Review spec completion"}
				</button>
			</div>
			{!readiness.readyForExecution && (
				<div className="operation-gate" id="completion-gate-reason">
					<strong>
						{readiness.stage === "adminReview"
							? "Waiting for administrator review"
							: "Waiting for an approved spec"}
					</strong>
					<span>
						{readiness.stage === "adminReview"
							? "Completion review unlocks after the administrator approves the revised spec."
							: "Finish spec discovery and administrator approval before reviewing implementation completion."}
					</span>
				</div>
			)}

			{latestSpecReview ? (
				<div
					className="completion-review-result"
					data-state={assessment?.verdict === "notComplete" ? "notComplete" : latestSpecReview.status}
				>
					<div className="execution-heading">
						<strong>Latest assessment</strong>
						<span className="project-state">{latestSpecReviewStatus}</span>
					</div>
					{assessment && (
						<div className="completion-verdict" data-verdict={assessment.verdict}>
							<strong>{assessment.verdict === "complete" ? "Spec complete" : "Spec not complete"}</strong>
							{assessment.commitRange && <span>Reviewed commits {assessment.commitRange}</span>}
						</div>
					)}
					<p>
						{latestSpecReview.sanitizedSummary ??
							(latestSpecReview.status === "queued" || latestSpecReview.status === "running"
								? "Comparing the approved spec with the current checkout and Git history…"
								: "The review finished without a readable assessment.")}
					</p>
					{latestSpecReview.completionAssessment && !assessmentIsCurrent && (
						<small>
							This assessment belongs to an earlier approved Spec. Run a new review for the current acceptance
							checklist.
						</small>
					)}
				</div>
			) : (
				<div className="completion-empty">
					<strong>No completion assessment yet.</strong>
					<p>Run a read-only review to establish what is met, blocked, or still needs validation.</p>
				</div>
			)}

			{assessment && counts && (
				<>
					<div className="completion-counts" role="list" aria-label="Acceptance criterion totals">
						{Object.entries(COMPLETION_STATUS_LABELS).map(([key, label]) => (
							<div key={key} role="listitem" data-status={key}>
								<strong>{counts[key] ?? 0}</strong>
								<span>{label}</span>
							</div>
						))}
					</div>
					<div className="completion-ledger">
						<table>
							<caption>Acceptance criteria review</caption>
							<thead>
								<tr>
									<th scope="col">Spec</th>
									<th scope="col">Acceptance criterion</th>
									<th scope="col">Status</th>
									<th scope="col">Evidence / next action</th>
								</tr>
							</thead>
							<tbody>
								{assessment.criteria.map((criterion, index) => (
									<tr key={`${criterion.spec}-${index}`}>
										<td data-label="Spec">
											<RichText content={criterion.spec} inline />
										</td>
										<td data-label="Acceptance criterion">
											<RichText content={criterion.acceptanceCriterion} inline />
										</td>
										<td data-label="Status">
											<span className="completion-status" data-status={criterion.status}>
												{COMPLETION_STATUS_LABELS[criterion.status]}
											</span>
										</td>
										<td data-label="Evidence / next action">
											<p>
												<RichText content={criterion.evidence} inline />
											</p>
											{criterion.ownerEvidencePending && (
												<small>
													<strong>Waiting on owner evidence.</strong>
												</small>
											)}
											{criterion.nextAction && (
												<small>
													<strong>Next:</strong> <RichText content={criterion.nextAction} inline />
												</small>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			)}

			{latestSpecReview?.status === "succeeded" && !assessment && (
				<small>Run a new review to generate the acceptance-criteria ledger.</small>
			)}
			{hasActiveExecution && latestSpecReview?.status !== "queued" && latestSpecReview?.status !== "running" && (
				<small>A completion review becomes available when the current project execution finishes.</small>
			)}
			{status && (
				<StatusBanner
					kind={status.includes("requested") ? "success" : status.includes("could not") ? "error" : "info"}
				>
					{status}
				</StatusBanner>
			)}
			{executionPage.hasMore && (
				<button
					type="button"
					className="secondary"
					disabled={executionPage.loadingMore}
					onClick={() => void executionPage.loadMore()}
				>
					{executionPage.loadingMore ? "Loading…" : "Load more reviews"}
				</button>
			)}
		</section>
	);
}

export function ProjectRequests({ projectId }: { projectId: string }) {
	const [kind, setKind] = useState<(typeof ACTIONS)[number][0]>("majorInfrastructure");
	const [reason, setReason] = useState("");
	const [status, setStatus] = useState("");

	async function requestAction() {
		setStatus("");
		try {
			await portalFetch(`/api/portal/v1/projects/${projectId}/action-requests`, {
				method: "POST",
				body: JSON.stringify({ kind, reason: reason.trim() }),
			});
			setReason("");
			setStatus("Request sent to Neal’s Inbox.");
		} catch {
			setStatus("The request could not be sent.");
		}
	}

	return (
		<section className="project-requests" aria-labelledby="owner-request-heading">
			<p className="eyebrow">NEEDS OWNER APPROVAL</p>
			<h2 id="owner-request-heading">Controlled changes</h2>
			<p>Infrastructure, permissions, release, and project lifecycle changes always go to Neal for a decision.</p>
			<PortalSelect
				id="action-request-kind"
				label="Request type"
				value={kind}
				options={ACTIONS.map(([value, label]) => ({ value, label }))}
				onChange={(value) => setKind(value as typeof kind)}
			/>
			<label htmlFor="action-request-reason">Why is this needed?</label>
			<textarea id="action-request-reason" value={reason} onChange={(event) => setReason(event.target.value)} />
			<button type="button" disabled={!reason.trim()} onClick={requestAction}>
				Send request
			</button>
			{status && <StatusBanner kind={status.includes("sent") ? "success" : "error"}>{status}</StatusBanner>}
		</section>
	);
}
